import React, { useState } from 'react';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import SectionIcon from '@/components/SectionIcon';
import NewExpenseModal from '@/components/modals/NewExpenseModal';
import { I } from '@/lib/icons';
import { fmtEur, fmtDate } from '@/lib/format';
import { CATEGORIES } from '@/lib/sections';
import { useWorkspace, useExpenses } from '@/hooks/useData';
import { useToast } from '@/components/Toast';

export default function Vydavky() {
  const { workspace } = useWorkspace();
  const { expenses, loading, remove } = useExpenses(workspace?.id);
  const toast = useToast();
  const [modal, setModal] = useState({ open: false, expense: null });
  const [yearFilter, setYearFilter] = useState('all');
  const [catFilter, setCatFilter] = useState('all');

  const years = Array.from(new Set(expenses.map(e => new Date(e.date).getFullYear()))).sort((a, b) => b - a);
  let list = expenses;
  if (yearFilter !== 'all') list = list.filter(e => new Date(e.date).getFullYear() === Number(yearFilter));
  if (catFilter !== 'all') list = list.filter(e => e.category === catFilter);

  const total = list.reduce((s, e) => s + Number(e.amount || 0), 0);

  const onDelete = async (id) => {
    if (!window.confirm('Naozaj odstrániť tento výdavok?')) return;
    try { await remove(id); toast.success('Vymazané.'); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <>
      <PageHeader title="Výdavky" meta={`${expenses.length} celkom · ${fmtEur(total)}`} accent="orange"
        right={
          <button onClick={() => setModal({ open: true, expense: null })}
            data-testid="new-expense-btn" className="cd-btn cd-btn-primary">
            <I.plus /> Nový výdavok
          </button>
        } />

      {/* Filters */}
      <div style={{ padding: '6px 32px 16px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => setCatFilter('all')} className={`cd-chip${catFilter === 'all' ? ' active' : ''}`}>Všetky kategórie</button>
        {Object.entries(CATEGORIES).map(([key, c]) => (
          <button key={key} onClick={() => setCatFilter(key)} className={`cd-chip${catFilter === key ? ' active' : ''}`}>
            {c.label}
          </button>
        ))}
        {years.length > 1 && (
          <>
            <div style={{ width: 1, background: 'var(--border)', margin: '0 4px' }} />
            <button onClick={() => setYearFilter('all')} className={`cd-chip${yearFilter === 'all' ? ' active' : ''}`}>Všetky roky</button>
            {years.map(y => (
              <button key={y} onClick={() => setYearFilter(String(y))} className={`cd-chip${yearFilter === String(y) ? ' active' : ''}`}>{y}</button>
            ))}
          </>
        )}
      </div>

      <div style={{ padding: '0 32px 32px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
            <span className="cd-spinner" /> Načítavam...
          </div>
        ) : list.length === 0 ? (
          <EmptyState icon="receipt" color="orange"
            title={expenses.length === 0 ? 'Zatiaľ žiadne výdavky' : 'Nič v tomto filtri'}
            desc={expenses.length === 0
              ? 'Pridaj prvý doklad — manuálne (OCR pridáme neskôr).'
              : 'Skús iný filter alebo pridaj nový výdavok.'}
            ctaLabel="Nový výdavok" onCta={() => setModal({ open: true, expense: null })} />
        ) : (
          <div style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--r-lg)', overflow: 'hidden',
          }}>
            {list.map((e, i) => {
              const cat = CATEGORIES[e.category] || CATEGORIES.other;
              return (
                <div key={e.id} data-testid={`expense-row-${e.id}`}
                  onClick={() => setModal({ open: true, expense: e })}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px',
                    borderBottom: i < list.length - 1 ? '1px solid var(--border)' : 'none',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={ev => ev.currentTarget.style.background = 'var(--bg-hover)'}
                  onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}>
                  <SectionIcon color={cat.color} size={36}>
                    {React.createElement(I[cat.icon] || I.other, { width: 17, height: 17 })}
                  </SectionIcon>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 2 }}>
                      {e.vendor_name || 'Bez dodávateľa'}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                      {cat.label} · {fmtDate(e.date)} · DPH {e.dph_rate}%
                      {e.description && ` · ${e.description}`}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, fontFamily: 'var(--font-display)' }}>
                      {fmtEur(e.amount)}
                    </div>
                  </div>
                  <button onClick={(ev) => { ev.stopPropagation(); onDelete(e.id); }}
                    data-testid={`expense-delete-${e.id}`}
                    style={{
                      background: 'transparent', border: 'none', color: 'var(--text-muted)',
                      padding: 6, cursor: 'pointer',
                    }}>
                    <I.trash />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <NewExpenseModal
        open={modal.open}
        expense={modal.expense}
        onClose={() => setModal({ open: false, expense: null })}
      />
    </>
  );
}
